import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import ConfirmModal from './ConfirmModal';

const normalizePhoneValue = (value = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) return '';
  return hasPlus ? `+${digits}` : digits;
};

const formatPhoneForDisplay = (value = '') => {
  const normalized = normalizePhoneValue(value);
  if (!normalized) return '';
  return normalized;
};

const deriveContactKey = (phone = '', email = '') => {
  const normalizedPhone = normalizePhoneValue(phone);
  if (normalizedPhone) return normalizedPhone;
  if (!email) return '';
  const idx = email.lastIndexOf('.');
  return idx > 0 ? email.substring(0, idx) : email;
};

const ShareListModal = ({ open, onClose, listItems, listName, totalValue, currency }) => {
  const { t } = useTranslation();
  const { uid } = useUser();
  const { theme: themeData, themeMode } = useTheme();
  const [mode, setMode] = React.useState('share'); // 'share', 'newContact', 'editContact'
  const [contacts, setContacts] = React.useState([]);
  const [filteredContacts, setFilteredContacts] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedContact, setSelectedContact] = React.useState(null);
  const [selectedEditContact, setSelectedEditContact] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  // Form state for new/edit contact
  const [contactForm, setContactForm] = React.useState({
    name: '',
    phone: '',
    email: '',
    originalPhone: '',
    originalEmail: ''
  });

  const functions = React.useMemo(() => getFunctions(getApp(), 'us-central1'), []);

  // Simple loading spinner
  const Spinner = ({ size = 20 }) => (
    <svg
      className="animate-spin text-slate-300"
      style={{ width: size, height: size }}
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );

  // Load contacts function
  const loadContacts = React.useCallback(async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const userContactsRef = doc(db, 'userContacts', uid);
      const snap = await getDoc(userContactsRef);

      if (snap.exists()) {
        const data = snap.data();
        // Convert object format to array and sort by name
        const contactsArray = Object.values(data).map(contact => {
          const normalizedPhone = normalizePhoneValue(contact.phone);
          const key = deriveContactKey(contact.phone, contact.email);
          return {
            ...contact,
            phone: normalizedPhone,
            displayPhone: formatPhoneForDisplay(normalizedPhone),
            key,
          };
        }).sort((a, b) => a.name.localeCompare(b.name));
        setContacts(contactsArray);
      } else {
        setContacts([]);
      }
    } catch (err) {
      setError(t('shareList.failedToLoadContacts'));
    } finally {
      setLoading(false);
    }
  }, [uid, t]);

  // Load contacts when modal opens
  React.useEffect(() => {
    if (open && uid) {
      loadContacts();
    }
  }, [open, uid, loadContacts]);

  // Filter contacts based on search
  React.useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContacts(contacts);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredContacts(contacts.filter(contact =>
        contact.name.toLowerCase().includes(term) ||
        contact.phone?.toLowerCase().includes(term) ||
        contact.email?.toLowerCase().includes(term)
      ));
    }
  }, [contacts, searchTerm]);

  // Reset form and selections when mode changes
  React.useEffect(() => {
    resetFormAndSelections();
  }, [mode]);

  const resetFormAndSelections = () => {
    setContactForm({
      name: '',
      phone: '',
      email: '',
      originalPhone: '',
      originalEmail: ''
    });
    setSelectedContact(null);
    setSelectedEditContact(null);
    setSearchTerm('');
    setError('');
    setSuccess('');
  };

  const validateContactForm = () => {
    if (!contactForm.name.trim()) {
      setError(t('shareList.nameRequired'));
      return false;
    }
    if (!contactForm.phone.trim() && !contactForm.email.trim()) {
      setError(t('shareList.phoneOrEmailRequired'));
      return false;
    }
    if (contactForm.email.trim() && !isValidEmail(contactForm.email)) {
      setError(t('shareList.invalidEmail'));
      return false;
    }
    if (contactForm.phone.trim() && !isValidPhone(contactForm.phone)) {
      setError(t('shareList.invalidPhone'));
      return false;
    }
    return true;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    const normalized = normalizePhoneValue(phone);
    return normalized.length >= 7 && /^\+?\d+$/.test(normalized);
  };

  const addContact = async () => {
    if (!validateContactForm()) return;

    try {
      setLoading(true);
      setError('');

      const userContactsRef = doc(db, 'userContacts', uid);
      const snap = await getDoc(userContactsRef);

      let newData = snap.exists() ? snap.data() : {};

      const { name, phone, email } = contactForm;
      const normalizedPhone = normalizePhoneValue(phone);
      const key = normalizedPhone || (email ? email.substring(0, email.lastIndexOf('.')) : '');

      // Check if contact already exists
      if (newData[key]) {
        setError(t('shareList.contactAlreadyExists'));
        return;
      }

      // Add new contact
      const contactData = { name: name.trim() };
      if (normalizedPhone) contactData.phone = normalizedPhone;
      if (email.trim()) contactData.email = email.trim();

      newData[key] = contactData;

      await setDoc(userContactsRef, newData, { merge: true });
      setSuccess(t('shareList.contactAdded'));
      setContactForm({ name: '', phone: '', email: '', originalPhone: '', originalEmail: '' });
      loadContacts();
      setTimeout(() => {
        setSuccess('');
        setMode('share');
      }, 2000);
    } catch (err) {
      setError(t('shareList.failedToAddContact'));
    } finally {
      setLoading(false);
    }
  };

  const editContact = async () => {
    if (!validateContactForm()) return;

    try {
      setLoading(true);
      setError('');

      const userContactsRef = doc(db, 'userContacts', uid);
      const snap = await getDoc(userContactsRef);

      if (!snap.exists()) {
        setError(t('shareList.contactNotFound'));
        return;
      }

      let newData = snap.data();
      const { name, phone, email, originalPhone, originalEmail } = contactForm;

      // Remove old entry
      const oldKey = deriveContactKey(originalPhone, originalEmail);
      if (oldKey && newData[oldKey]) {
        delete newData[oldKey];
      }

      // Add updated entry
      const normalizedPhone = normalizePhoneValue(phone);
      const newKey = deriveContactKey(phone, email);
      const contactData = { name: name.trim() };
      if (normalizedPhone) contactData.phone = normalizedPhone;
      if (email.trim()) contactData.email = email.trim();

      newData[newKey] = contactData;

      await setDoc(userContactsRef, newData);
      setSuccess(t('shareList.contactUpdated'));
      setContactForm({ name: '', phone: '', email: '', originalPhone: '', originalEmail: '' });
      loadContacts();
      setTimeout(() => {
        setSuccess('');
        setMode('share');
      }, 2000);
    } catch (err) {
      setError(t('shareList.failedToUpdateContact'));
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async () => {
    if (!selectedEditContact) return;
    setDeleteConfirmOpen(false);

    try {
      setLoading(true);
      setError('');

      const userContactsRef = doc(db, 'userContacts', uid);
      const snap = await getDoc(userContactsRef);

      if (!snap.exists()) {
        setError(t('shareList.contactNotFound'));
        return;
      }

      let newData = snap.data();
      const { originalPhone, originalEmail } = contactForm;

      // Remove contact
      const keyToDelete = deriveContactKey(originalPhone, originalEmail);
      if (keyToDelete && newData[keyToDelete]) {
        delete newData[keyToDelete];
        await setDoc(userContactsRef, newData);
        setSuccess(t('shareList.contactDeleted'));
        loadContacts();
        setTimeout(() => {
          setSuccess('');
          setMode('share');
        }, 2000);
      } else {
        setError(t('shareList.contactNotFound'));
      }
    } catch (err) {
      setError(t('shareList.failedToDeleteContact'));
    } finally {
      setLoading(false);
    }
  };

  const shareList = async () => {
    if (!selectedContact || !listItems?.length) {
      setError(t('shareList.selectContactAndEnsureItems'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const shareUserList = httpsCallable(functions, 'shareUserList2', { timeout: 1080000 });
      const listInfo = {
        name: listName || 'Buying List',
        totalValue: totalValue || 0,
        currency: currency || 'USD',
        itemCount: listItems.length
      };

      const payload = {
        list: listItems,
        info: listInfo,
        title: listName
      };

      if (selectedContact.phone) {
        payload.phoneTarget = normalizePhoneValue(selectedContact.phone);
      } else if (selectedContact.email) {
        payload.emailTarget = selectedContact.email;
      }

      const result = await shareUserList(payload);
      if (result.data?.success) {
        const successMessage = result.data?.message || t('shareList.listSharedSuccess');
        setSuccess(successMessage);
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 2000);
      } else {
        const errorMessage = result.data?.message || t('shareList.failedToShareList');
        setError(errorMessage);
      }
    } catch (err) {
      // Map server error messages to translation keys
      const serverMessage = err.message || err.details || '';
      let errorMessage = t('shareList.failedToShareList');

      if (serverMessage.includes('User not found') || serverMessage.includes('not-found')) {
        errorMessage = t('shareList.errorUserNotFound');
      } else if (serverMessage.includes('unauthenticated') || serverMessage.includes('authenticated')) {
        errorMessage = t('shareList.errorNotAuthenticated');
      } else if (serverMessage.includes('List is required') || serverMessage.includes('invalid-argument')) {
        errorMessage = t('shareList.errorInvalidList');
      } else if (serverMessage.includes('phone number or email')) {
        errorMessage = t('shareList.errorMissingContact');
      } else if (serverMessage.includes('internal') || serverMessage.includes('error occurred')) {
        errorMessage = t('shareList.errorServerError');
      } else if (serverMessage) {
        errorMessage = serverMessage;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setMode('share');
    resetFormAndSelections();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleEditContact = (contact) => {
    setSelectedEditContact(contact);
    setContactForm({
      name: contact.name,
      phone: contact.phone || '',
      email: contact.email || '',
      originalPhone: contact.phone || '',
      originalEmail: contact.email || ''
    });
  };

  const handleModeChange = (newMode) => {
    if (newMode !== mode) {
      setMode(newMode);
    }
  };

  if (!open) return null;

  const isForest = themeMode === 'forest';

  const cardStyle = {
    backgroundColor: themeData.surface.card,
    border: `1px solid ${themeData.surface.cardBorder}`,
    boxShadow: `0 4px 12px ${themeData.shadow.md}`,
  };

  const inputStyle = {
    backgroundColor: themeData.input.background,
    border: `1px solid ${themeData.input.border}`,
    color: themeData.text.primary,
  };

  const tabStyle = (active) => ({
    backgroundColor: active ? themeData.interactive.primary : 'transparent',
    color: active ? '#ffffff' : themeData.text.secondary,
  });

  const contactCardStyle = (selected) => ({
    backgroundColor: selected
      ? (isForest ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
      : (isForest ? themeData.surface.elevated : 'rgba(0,0,0,0.03)'),
    border: `1px solid ${selected ? themeData.interactive.primary : themeData.surface.cardBorder}`,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="rounded-xl max-w-md w-full max-h-[90vh] overflow-hidden" style={cardStyle}>
        {/* Header */}
        <div className="p-4" style={{ borderBottom: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : themeData.surface.cardBorder}` }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: isForest ? '#e8f5e9' : themeData.text.primary }}>{t('shareList.title')}</h2>
            <button
              onClick={handleClose}
              style={{ color: isForest ? '#a5d6a7' : themeData.text.tertiary }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Tab buttons */}
          <div className="flex rounded-lg p-1" style={{ backgroundColor: isForest ? 'rgba(15,58,48,0.5)' : 'rgba(0,0,0,0.05)' }}>
            <button
              onClick={() => handleModeChange('share')}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors"
              style={tabStyle(mode === 'share')}
            >
              {t('shareList.shareListTab')}
            </button>
            <button
              onClick={() => handleModeChange('newContact')}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors"
              style={tabStyle(mode === 'newContact')}
            >
              {t('shareList.newContactTab')}
            </button>
            <button
              onClick={() => handleModeChange('editContact')}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors"
              style={tabStyle(mode === 'editContact')}
            >
              {t('shareList.editContactTab')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="mb-3 p-2 rounded text-sm" style={{ backgroundColor: 'rgba(220,38,38,0.2)', border: '1px solid rgba(220,38,38,0.5)', color: themeData.status.error }}>
              {error}
            </div>
          )}

          {success && (
            <div className="mb-3 p-2 rounded text-sm" style={{ backgroundColor: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.5)', color: themeData.status.success }}>
              {success}
            </div>
          )}

          {mode === 'share' && (
            <div className="space-y-4">
              {/* Search */}
              {!loading && (
                <input
                  type="text"
                  placeholder={t('shareList.searchContacts')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                />
              )}

              {/* Contacts list */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center py-6"><Spinner size={24} /></div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-4" style={{ color: isForest ? '#a5d6a7' : themeData.text.tertiary }}>
                    {contacts.length === 0 ? t('shareList.noContactsFound') : t('shareList.noMatchingContacts')}
                  </div>
                ) : (
                  filteredContacts.map((contact, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedContact(contact)}
                      className="p-3 rounded-md cursor-pointer transition-colors"
                      style={contactCardStyle(selectedContact === contact)}
                    >
                      <div className="font-medium" style={{ color: isForest ? '#e8f5e9' : themeData.text.primary }}>{contact.name}</div>
                      <div className="text-sm" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>
                        {contact.phone && <div>{contact.displayPhone || contact.phone}</div>}
                        {contact.email && <div>{contact.email}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {mode === 'newContact' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.nameLabel')} *</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder={t('shareList.contactNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.phoneLabel')}</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.emailLabel')}</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="text-xs" style={{ color: isForest ? '#a5d6a7' : themeData.text.tertiary }}>
                {t('shareList.requiredFieldsNote')}
              </div>
            </div>
          )}

          {mode === 'editContact' && !selectedEditContact && (
            <div className="space-y-2">
              <div className="text-sm mb-2" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.selectContactToEdit')}</div>
              {loading ? (
                <div className="flex justify-center py-6"><Spinner size={24} /></div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-4" style={{ color: isForest ? '#a5d6a7' : themeData.text.tertiary }}>{t('shareList.noContactsFound')}</div>
              ) : (
                contacts.map((contact, index) => (
                  <div
                    key={index}
                    onClick={() => handleEditContact(contact)}
                    className="p-3 rounded-md cursor-pointer transition-colors"
                    style={contactCardStyle(false)}
                  >
                    <div className="font-medium" style={{ color: isForest ? '#e8f5e9' : themeData.text.primary }}>{contact.name}</div>
                    <div className="text-sm" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>
                      {contact.phone && <div>{contact.phone}</div>}
                      {contact.email && <div>{contact.email}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {mode === 'editContact' && selectedEditContact && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.nameLabel')} *</label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder={t('shareList.contactNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.phoneLabel')}</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}>{t('shareList.emailLabel')}</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full p-2 rounded"
                  style={inputStyle}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="text-xs" style={{ color: isForest ? '#a5d6a7' : themeData.text.tertiary }}>
                {t('shareList.requiredFieldsNote')}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex justify-end gap-2" style={{ borderTop: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : themeData.surface.cardBorder}` }}>
          <button
            onClick={handleClose}
            className="px-4 py-2 transition-colors"
            style={{ color: isForest ? '#a5d6a7' : themeData.text.secondary }}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>

          {mode === 'share' && (
            <button
              onClick={shareList}
              disabled={!selectedContact || loading || !listItems?.length}
              className="px-4 py-2 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: themeData.interactive.primary }}
            >
              {loading ? t('shareList.sharing') : t('shareList.shareListButton')}
            </button>
          )}

          {mode === 'newContact' && (
            <button
              onClick={addContact}
              disabled={loading}
              className="px-4 py-2 text-white rounded-md disabled:opacity-50 font-medium"
              style={{ backgroundColor: '#22c55e' }}
            >
              {loading ? t('shareList.adding') : t('shareList.addContactButton')}
            </button>
          )}

          {mode === 'editContact' && selectedEditContact && contactForm.name && (
            <>
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={loading}
                className="px-4 py-2 text-white rounded-md disabled:opacity-50 font-medium"
                style={{ backgroundColor: '#ef4444' }}
              >
                {loading ? t('shareList.deleting') : t('shareList.deleteContactButton')}
              </button>
              <button
                onClick={editContact}
                disabled={loading}
                className="px-4 py-2 text-white rounded-md disabled:opacity-50 font-medium"
                style={{ backgroundColor: '#f97316' }}
              >
                {loading ? t('shareList.updating') : t('shareList.updateContactButton')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete Contact Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={deleteContact}
        title={t('shareList.deleteContactButton')}
        message={t('shareList.confirmDeleteContact', { name: selectedEditContact?.name || '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive={true}
      />
    </div>
  );
};

export default ShareListModal;
